import * as Sentry from '@sentry/angular';
import { environment } from '@environments/environment';

export function initSentry(): void {
    Sentry.init({
        dsn: environment.sentry_dsn,
        release: environment.sentryRelease,
        environment: environment.sentry_environment,
        enabled: environment.production,
        enableLogs: true,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
            Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
        ],
        tracesSampleRate: 0.2,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,

        ignoreErrors: [
            // Lazy-loaded chunk failed to load (user on old deploy, fixed by refresh)
            /Loading chunk [\d]+ failed/,
            // Benign browser/DOM warning, not actionable
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
            // Cross-origin scripts (browser extensions, injected scripts)
            'Script error.',
            // Network cancellations (user navigated away mid-request)
            'AbortError',
            /^NetworkError/,
            // PrimeNG / Angular internal hydration noise
            /ExpressionChangedAfterItHasBeenCheckedError/,
            // Mercure SSE reconnection events — handled automatically by EventSource
            /EventSource/i,
            // Mapbox GL JS worker bundle minification bug (blob: worker script) — no user impact, not fixable in first-party code
            /Can't find variable: On/,
            /^On is not defined/,
        ],

        denyUrls: [
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            /^moz-extension:\/\//i,
            // Mercure SSE endpoint — connection drops are expected noise
            /\.well-known\/mercure/i,
        ],

        beforeSendLog(log) {
            if (/mercure|EventSource/i.test(log.message)) return null;
            return log;
        },

        beforeSend(event, hint) {
            const original = hint?.originalException;
            if (original && typeof original === 'object' && (original as Record<string, unknown>)['shouldLogout'] === true) return null;
            const hasStack = event.exception?.values?.some((e) => e.stacktrace?.frames?.length);
            if (!hasStack) return null;
            const isMercure = event.exception?.values?.some((e) => /mercure|EventSource/i.test(e.value ?? ''));
            if (isMercure) return null;
            // Mapbox GL JS worker race condition on RTL plugin init — no user impact on LTR languages
            const isMapboxRTL = event.exception?.values?.some((e) =>
                /syncRTLPluginState/i.test(e.stacktrace?.frames?.map((f) => f.function).join(' ') ?? ''),
            );
            if (isMapboxRTL) return null;
            // Errors entirely originating from a blob: worker bundle (e.g. Mapbox GL JS) — no first-party code involved, not fixable here
            const isBlobOnlyStack = event.exception?.values?.every((e) => {
                const frames = e.stacktrace?.frames;
                return !!frames?.length && frames.every((f) => f.filename?.startsWith('blob:'));
            });
            if (isBlobOnlyStack) return null;
            return event;
        },
    });
}
